import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { negotiationAPI } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MessageSquare, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NegotiationCard, NegotiationDetailDialog } from "@/components/negotiations";

export default function NegotiationsPage() {
  const { user } = useAuth();
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all, active, completed

  const fetchNegotiations = useCallback(async () => {
    try {
      const response = await negotiationAPI.getAll();
      setNegotiations(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Failed to fetch negotiations:", error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNegotiations();
  }, [fetchNegotiations]);

  const handleOpenDetail = async (negotiation) => {
    try {
      const response = await negotiationAPI.getOne(negotiation.id);
      setSelectedNegotiation(response.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error("Failed to load negotiation details");
    }
  };

  const handleUpdate = async () => {
    // Refresh both the selected negotiation and the list
    await fetchNegotiations();
    if (selectedNegotiation) {
      try {
        const response = await negotiationAPI.getOne(selectedNegotiation.id);
        setSelectedNegotiation(response.data);
      } catch {
        setDetailOpen(false);
      }
    }
  };

  const filteredNegotiations = negotiations.filter(n => {
    if (filter === "active") return ["pending", "countered"].includes(n.status);
    if (filter === "completed") return ["accepted", "rejected", "expired"].includes(n.status);
    return true;
  });

  const activeCount = negotiations.filter(n => ["pending", "countered"].includes(n.status)).length;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight mb-2">Negotiations</h1>
          <p className="text-muted-foreground">
            Manage your price negotiations with {user?.user_type === "partner" ? "customers" : "partners"}
          </p>
        </div>
        {activeCount > 0 && (
          <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
            {activeCount} active
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          className="rounded-none"
          onClick={() => setFilter("all")}
        >
          All ({negotiations.length})
        </Button>
        <Button
          variant={filter === "active" ? "default" : "outline"}
          size="sm"
          className="rounded-none"
          onClick={() => setFilter("active")}
        >
          Active ({activeCount})
        </Button>
        <Button
          variant={filter === "completed" ? "default" : "outline"}
          size="sm"
          className="rounded-none"
          onClick={() => setFilter("completed")}
        >
          Completed ({negotiations.length - activeCount})
        </Button>
      </div>

      {/* Negotiations List */}
      {filteredNegotiations.length === 0 ? (
        <div className="border border-border p-12 text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
          <h3 className="font-bold text-lg mb-2">No negotiations yet</h3>
          <p className="text-muted-foreground text-sm">
            {user?.user_type === "partner" 
              ? "When customers make offers on your listings, they'll appear here"
              : "Make an offer on a cargo or jet listing to start negotiating"
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNegotiations.map(negotiation => (
            <NegotiationCard
              key={negotiation.id}
              negotiation={negotiation}
              currentUserId={user?.user_id}
              onClick={() => handleOpenDetail(negotiation)}
            />
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <NegotiationDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        negotiation={selectedNegotiation}
        currentUserId={user?.user_id}
        onUpdate={handleUpdate}
      />
    </div>
  );
}
